import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { getLogoUrlSync, getLogoUrl } from '../../../utils/logoUtils';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow-x: hidden;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
  padding: 20px 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(74, 108, 247, 0.3);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Logo = styled.img`
  height: 50px;
  object-fit: contain;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
`;

const HeaderTitle = styled.h1`
  color: white;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    text-align: center;
  }
`;


const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateX(-3px);
  }
`;

const MainContent = styled.main`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px 60px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const DocumentCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const DocumentHeader = styled.div`
  background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
  padding: 30px 40px;
  color: white;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const DocumentTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const DocumentSubtitle = styled.p`
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
`;

const DocumentBody = styled.div`
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const IntroText = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #334155;
  margin-bottom: 28px;
  padding: 20px 24px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #4a6cf7;
    border-radius: 3px 0 0 3px;
  }
`;

const Section = styled.section`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: linear-gradient(180deg, #4a6cf7 0%, #2b3bdf 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SectionContent = styled.div`
  font-size: 15px;
  line-height: 1.8;
  color: #475569;
  padding-left: 16px;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0;
`;

const BulletItem = styled.li`
  position: relative;
  padding-left: 28px;
  margin-bottom: 12px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
    border-radius: 50%;
  }
  
  strong {
    color: #1e293b;
  }
`;

const HighlightBox = styled.div<{ $variant?: 'warning' | 'info' | 'success' }>`
  padding: 14px 18px;
  border-radius: 6px;
  margin: 14px 0;
  font-size: 14px;
  line-height: 1.6;
  
  ${props => {
    switch (props.$variant) {
      case 'warning':
        return `
          background: #fffbeb;
          border-left: 3px solid #f59e0b;
          color: #78350f;
        `;
      case 'success':
        return `
          background: #f0fdf4;
          border-left: 3px solid #22c55e;
          color: #14532d;
        `;
      default:
        return `
          background: #f0f9ff;
          border-left: 3px solid #3b82f6;
          color: #1e3a5f;
        `;
    }
  }}
`;

const MoneyHighlight = styled.span`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  color: #92400e;
`;

const Divider = styled.hr`
  border: none;
  height: 2px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 40px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 14px;
  
  th, td {
    border: 1px solid #e2e8f0;
    padding: 12px;
    text-align: left;
  }
  
  th {
    background: #f1f5f9;
    font-weight: 600;
    color: #1e293b;
  }
  
  td {
    color: #475569;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    
    th, td {
      padding: 8px;
    }
  }
`;

const ImportantNote = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #fefce8;
  border-left: 4px solid #eab308;
  border-radius: 0 8px 8px 0;
  
  p {
    margin: 0 0 8px 0;
    font-size: 14px;
    line-height: 1.6;
    color: #713f12;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CorporateAddress = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.8;
    color: #334155;
  }
  
  strong {
    color: #1e293b;
  }
`;

const Footer = styled.footer`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  padding: 30px 24px;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 14px;
  opacity: 0.8;
  margin: 0;
  
  a {
    color: #60a5fa;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AcceptButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 16px 40px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 32px auto 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }
`;

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState<string>(getLogoUrlSync());

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const logo = await getLogoUrl();
        setLogoUrl(logo);
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    fetchLogo();
  }, []);

  const handleAccept = () => {
    navigate('/');
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Logo src={logoUrl} alt="MNGL Logo" />
          <HeaderTitle>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            MNGL DODO Dealership Guidelines {new Date().getFullYear()}
          </HeaderTitle>
          <BackButton onClick={() => navigate('/')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Application
          </BackButton>
        </HeaderContent>
      </Header>

      <MainContent>
        <DocumentCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <DocumentHeader>
            <DocumentTitle>Terms and Conditions</DocumentTitle>
            <DocumentSubtitle>
              MNGL DODO CNG Station Application Portal
            </DocumentSubtitle>
          </DocumentHeader>

          <DocumentBody>
            <IntroText>
              <strong>MAHARASHTRA NATURAL GAS LIMITED</strong><br />
              <em>(A Joint Venture of GAIL (India) Ltd. and BPCL)</em><br /><br />
              MNGL proposes to appoint dealers for setting up CNG Stations at various locations within Pune Geographical Areas. Applicant possessing and/or offering suitable land to MNGL on a long-term lease basis are invited to apply. The offered land and applicants shall be evaluated as per MNGL's prescribed process for grant of CNG Dealership.<br /><br />
              <em>(Details of advertised locations are provided in the DODO Application Forms.)</em>
            </IntroText>

            {/* Section 1 */}
            <Section>
              <SectionTitle>1. Minimum Plot Size Requirements</SectionTitle>
              <SectionContent>
                <Table>
                  <thead>
                    <tr>
                      <th>Road Category</th>
                      <th>Minimum Frontage of Offered Plot / Land</th>
                      <th>Depth of Offered Plot / Land</th>
                      <th>Minimum Total Area of Offered Plot / Land</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Within City</td>
                      <td>20 meters (66 feet)</td>
                      <td>31 meters (102 feet)</td>
                      <td>620 sq. meters (6,732 sq. feet)</td>
                    </tr>
                    <tr>
                      <td>Road Other Than National Highway (Non-NH)</td>
                      <td>31 meters (102 feet)</td>
                      <td>31 meters (102 feet)</td>
                      <td>961 sq. meters (10,404 sq. feet)</td>
                    </tr>
                    <tr>
                      <td>National Highway (Undivided)</td>
                      <td>35 meters (115 feet)</td>
                      <td>35 meters (115 feet)</td>
                      <td>1,225 sq. meters (13,225 sq. feet)</td>
                    </tr>
                    <tr>
                      <td>National Highway (Divided)</td>
                      <td>35 meters (115 feet)</td>
                      <td>45 meters (148 feet)</td>
                      <td>1,575 sq. meters (17,020 sq. feet)</td>
                    </tr>
                  </tbody>
                </Table>
                <p><em>Preference shall be given to plots having larger frontage.</em></p>
              </SectionContent>
            </Section>

            {/* Section 2 */}
            <Section>
              <SectionTitle>2. Access Road</SectionTitle>
              <SectionContent>
                <p>The approach road to the plot must have a minimum width of 12 meters.</p>
              </SectionContent>
            </Section>

            {/* Section 3 */}
            <Section>
              <SectionTitle>3. Land Title</SectionTitle>
              <SectionContent>
                <p>The offered plot must have a clear, marketable, and undisputed title and be legally suitable for setting up a CNG Station.</p>
              </SectionContent>
            </Section>

            {/* Section 4 */}
            <Section>
              <SectionTitle>4. Ownership / Lease Conditions</SectionTitle>
              <SectionContent>
                <BulletList>
                  <BulletItem>
                    Applicants who own the land or land is co-owned by the Applicant or land is owned by the relative of the applicant/partner of applicant/relative of partner of applicant or applicant having long term lease of the land or applicant willing to offer long term lease of the land are eligible.
                  </BulletItem>
                  <BulletItem>
                    Lease period shall be minimum 15 years up to 28 years, 11 months, and 30 days.
                  </BulletItem>
                  <BulletItem>
                    Applicants willing to sub-lease the land to MNGL shall be given preference over applicants having only a firm offer from third parties.
                  </BulletItem>
                  <BulletItem>
                    In case of leased land, the lease deed must explicitly permit sub-leasing to MNGL.
                  </BulletItem>
                </BulletList>
              </SectionContent>
            </Section>

            {/* Section 5 */}
            <Section>
              <SectionTitle>5. Site Evaluation</SectionTitle>
              <SectionContent>
                <p>The suitability of the land shall be assessed based on technical feasibility, commercial viability, location advantages.</p>
              </SectionContent>
            </Section>

            {/* Section 6 */}
            <Section>
              <SectionTitle>6. Security Deposit</SectionTitle>
              <SectionContent>
                <p>The security deposit shall comprise:</p>
                <BulletList>
                  <BulletItem>
                    <strong>Non-refundable License Fee:</strong> ₹15,00,000 (inclusive of applicable taxes), payable before issuance of LOI.
                  </BulletItem>
                  <BulletItem>
                    <strong>Fixed Security Deposit:</strong> ₹5,00,000, refundable without interest, payable prior to signing of DSL Agreement.
                  </BulletItem>
                  <BulletItem>
                    <strong>Variable Security Deposit:</strong>
                    <BulletList>
                      <BulletItem>Bank Guarantee equivalent to Average CNG Sales × Current CNG RSP × 5 days</BulletItem>
                    </BulletList>
                    <p style={{ textAlign: 'center', fontWeight: 'bold', margin: '8px 0' }}>OR</p>
                    <BulletList>
                      <BulletItem>Cash deposit and BG in 50:50 ratio calculated as Average CNG Sales × Current CNG RSP × 3 days.</BulletItem>
                      <BulletItem>The BG value shall be subject to periodic review based on assessed CNG sales potential.</BulletItem>
                    </BulletList>
                  </BulletItem>
                </BulletList>
              </SectionContent>
            </Section>

            {/* Section 7 */}
            <Section>
              <SectionTitle>7. Information Brochure</SectionTitle>
              <SectionContent>
                <p>Applicants are advised to carefully read all details provided in the Information Brochure available on MNGL's website: <a href="https://www.mngl.in" target="_blank" rel="noopener noreferrer">www.mngl.in</a>.</p>
              </SectionContent>
            </Section>

            {/* Section 8 */}
            <Section>
              <SectionTitle>8. Application Submission</SectionTitle>
              <SectionContent>
                <p>Applications shall be submitted online through the MNGL portal. Hard copies of the application along with required documents must be submitted to the MNGL Corporate Office within 10 days of online submission.</p>
                
                <CorporateAddress>
                  <p><strong>Corporate Office Address:</strong></p>
                  <p>Maharashtra Natural Gas Limited</p>
                  <p>Marketing Department, 3rd Floor</p>
                  <p>Pride Purple Coronet, Baner Road</p>
                  <p>Baner, Pune – 411045</p>
                </CorporateAddress>
              </SectionContent>
            </Section>

            <ImportantNote>
              <p>➤ MNGL reserves the right to cancel/withdraw/amend this advertisement or extend the due date at its sole discretion without assigning any reason or issuing any notice (the communication in this regards shall be made only on our website) and no claims of whatsoever nature on behalf of the applicant shall be entertained.</p>
              <p>➤ This is purely a business proposition and not an application for job in MNGL and has normal business risks and also does not guarantee assured returns or profits.</p>
              <p>➤ This is only a notice inviting application for allotment of CNG dealership & not an offer of dealership.</p>
              <p>➤ The preference will be given to Govt. /Govt. Controlled Agencies/Co-operative Bodies in overall selection process.</p>
              <p>➤ Brokers/Property Dealers need not apply.</p>
            </ImportantNote>

            <AcceptButton onClick={handleAccept}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              I Have Read and Understood
            </AcceptButton>
          </DocumentBody>
        </DocumentCard>
      </MainContent>

      <Footer>
        <FooterText>
          © {new Date().getFullYear()} Maharashtra Natural Gas Limited (MNGL). All rights reserved.
          <br />
          A Joint Venture of GAIL (India) Ltd. & BPCL | <a href="https://www.mngl.in" target="_blank" rel="noopener noreferrer">www.mngl.in</a>
        </FooterText>
      </Footer>
    </Container>
  );
};

export default TermsAndConditions;
